import { useState, useEffect } from 'react'
import { Container, Card, Button, Modal, Form, Alert, Badge, Row, Col, Table } from 'react-bootstrap'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import accountService from '../services/accountService'
import paymentService from '../services/paymentService'

function Settings() {
  const { user, logout, refreshUser } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [showEditModal, setShowEditModal] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false)

  const [editFormData, setEditFormData] = useState({
    name: '',
    email_address: ''
  })

  const [passwordFormData, setPasswordFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  })

  const [deleteConfirmation, setDeleteConfirmation] = useState('')
  const [paymentHistory, setPaymentHistory] = useState([])
  const [paymentLoading, setPaymentLoading] = useState(false)

  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (user) {
      setEditFormData({
        name: user.name || '',
        email_address: user.email_address || ''
      })
    }
  }, [user])

  // Handle payment success/cancel from URL params
  useEffect(() => {
    const paymentStatus = searchParams.get('payment')
    if (paymentStatus === 'success') {
      setSuccess('Payment successful! Your premium membership is now active.')
      refreshUser()
      // Clear the URL params
      setSearchParams({})
    } else if (paymentStatus === 'cancelled') {
      setError('Payment was cancelled.')
      setSearchParams({})
    }
  }, [searchParams, refreshUser, setSearchParams])

  const openEditModal = () => {
    setEditFormData({
      name: user.name || '',
      email_address: user.email_address || ''
    })
    setShowEditModal(true)
  }

  const handleUpdateAccount = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      await accountService.updateAccount({
        name: editFormData.name,
        email_address: editFormData.email_address
      })

      setSuccess('Account updated successfully!')
      setShowEditModal(false)
      await refreshUser()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update account')
    } finally {
      setLoading(false)
    }
  }

  const handleChangePassword = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    // Validate passwords match
    if (passwordFormData.new_password !== passwordFormData.confirm_password) {
      setError('New passwords do not match')
      setLoading(false)
      return
    }

    // Validate password strength
    if (passwordFormData.new_password.length < 8) {
      setError('Password must be at least 8 characters long')
      setLoading(false)
      return
    }

    try {
      await accountService.changePassword(
        passwordFormData.current_password,
        passwordFormData.new_password
      )

      setSuccess('Password changed successfully!')
      setShowPasswordModal(false)
      setPasswordFormData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      })
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      setError('Please type DELETE to confirm account deletion')
      return
    }

    setLoading(true)
    setError(null)

    try {
      await accountService.deleteAccount()
      logout()
      navigate('/')
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete account')
      setLoading(false)
    }
  }

  const handleUpgradeToPremium = async () => {
    setLoading(true)
    setError(null)

    try {
      const { checkout_url } = await paymentService.createCheckout()
      // Redirect to Stripe checkout
      window.location.href = checkout_url
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create checkout session')
      setLoading(false)
    }
  }

  const loadPaymentHistory = async () => {
    setPaymentLoading(true)
    try {
      const history = await paymentService.getPaymentHistory()
      setPaymentHistory(history)
      setShowPaymentHistoryModal(true)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load payment history')
    } finally {
      setPaymentLoading(false)
    }
  }

  if (!user) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">
          Please log in to access settings.
        </Alert>
      </Container>
    )
  }

  const premiumExpiry = user.premium_expires ? new Date(user.premium_expires) : null
  const isPremiumActive = user.premium && premiumExpiry && premiumExpiry > new Date()

  return (
    <Container className="mt-4">
      <h2 className="mb-4">Account Settings</h2>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

      <Row>
        <Col lg={8} className="order-2 order-lg-1">
          {/* Account Information */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Account Information</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <strong>Name:</strong> {user.name}
              </div>
              <div className="mb-3">
                <strong>Email:</strong> {user.email_address}
                {!user.activated && (
                  <Badge bg="warning" className="ms-2">Not Activated</Badge>
                )}
              </div>
              <div className="mb-3">
                <strong>Account Type:</strong>{' '}
                {isPremiumActive ? (
                  <Badge bg="success">Premium</Badge>
                ) : (
                  <Badge bg="secondary">Free</Badge>
                )}
              </div>
              {user.oauth_provider && (
                <div className="mb-3">
                  <strong>Linked Account:</strong>{' '}
                  <Badge bg="info">{user.oauth_provider}</Badge>
                </div>
              )}
              <div className="mb-3">
                <strong>Member Since:</strong>{' '}
                {new Date(user.created).toLocaleDateString()}
              </div>
              <Button variant="primary" onClick={openEditModal}>
                Edit Account
              </Button>
            </Card.Body>
          </Card>

          {/* Security */}
          {!user.oauth_provider && (
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Security</h5>
              </Card.Header>
              <Card.Body>
                <p className="text-muted">
                  Keep your account secure by using a strong password.
                </p>
                <Button variant="primary" onClick={() => setShowPasswordModal(true)}>
                  Change Password
                </Button>
              </Card.Body>
            </Card>
          )}

          {/* Premium Status */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Premium Membership</h5>
            </Card.Header>
            <Card.Body>
              {isPremiumActive ? (
                <>
                  <div className="mb-3">
                    <Badge bg="success" className="fs-6">Active Premium Member</Badge>
                  </div>
                  <p>
                    <strong>Benefits:</strong>
                  </p>
                  <ul>
                    <li>Unlimited profiles</li>
                    <li>Advanced search features</li>
                    <li>Priority support</li>
                    <li>API access</li>
                  </ul>
                  <p className="text-muted">
                    Expires: {premiumExpiry.toLocaleDateString()}
                  </p>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={loadPaymentHistory}
                    disabled={paymentLoading}
                  >
                    {paymentLoading ? 'Loading...' : 'View Payment History'}
                  </Button>
                </>
              ) : (
                <>
                  <p>Upgrade to premium for additional features:</p>
                  <ul>
                    <li>Unlimited profiles (free accounts limited to 2)</li>
                    <li>Advanced search and filtering</li>
                    <li>Priority customer support</li>
                    <li>API access for developers</li>
                  </ul>
                  <div className="d-flex flex-wrap align-items-center gap-2 gap-md-3">
                    <Button
                      variant="success"
                      onClick={handleUpgradeToPremium}
                      disabled={loading}
                    >
                      {loading ? 'Processing...' : 'Upgrade to Premium - $59.99/year'}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={loadPaymentHistory}
                      disabled={paymentLoading}
                    >
                      Payment History
                    </Button>
                  </div>
                  <p className="text-muted mt-2 small">
                    Secure payment powered by Stripe
                  </p>
                </>
              )}
            </Card.Body>
          </Card>

          {/* Danger Zone */}
          <Card className="mb-4 border-danger">
            <Card.Header className="bg-danger text-white">
              <h5 className="mb-0">Danger Zone</h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-3">
                Once you delete your account, there is no going back. This will permanently
                delete all your profiles, scores, and trust relationships.
              </p>
              <Button
                variant="outline-danger"
                onClick={() => setShowDeleteModal(true)}
              >
                Delete Account
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4} className="order-1 order-lg-2 mb-4 mb-lg-0">
          {/* Quick Stats */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Your Activity</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <div className="d-flex justify-content-between">
                  <span>Profiles:</span>
                  <strong>{user.profiles?.length || 0}</strong>
                </div>
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between">
                  <span>Account Status:</span>
                  <strong>
                    {user.activated ? (
                      <span className="text-success">Active</span>
                    ) : (
                      <span className="text-warning">Pending</span>
                    )}
                  </strong>
                </div>
              </div>
              <Button
                variant="outline-primary"
                size="sm"
                className="w-100"
                onClick={() => navigate('/profiles')}
              >
                Manage Profiles
              </Button>
            </Card.Body>
          </Card>

          {/* Help & Support */}
          <Card>
            <Card.Header>
              <h5 className="mb-0">Help & Support</h5>
            </Card.Header>
            <Card.Body>
              <p className="small text-muted mb-2">
                Need help? Check out our resources:
              </p>
              <div className="d-grid gap-2">
                <Button variant="outline-secondary" size="sm" disabled>
                  Documentation
                </Button>
                <Button variant="outline-secondary" size="sm" disabled>
                  Contact Support
                </Button>
                <Button variant="outline-secondary" size="sm" disabled>
                  Privacy Policy
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Edit Account Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Account</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdateAccount}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                value={editFormData.name}
                onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email Address *</Form.Label>
              <Form.Control
                type="email"
                value={editFormData.email_address}
                onChange={(e) => setEditFormData({...editFormData, email_address: e.target.value})}
                required
              />
              <Form.Text className="text-muted">
                Changing your email will require re-activation.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Change Password Modal */}
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Change Password</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleChangePassword}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Current Password *</Form.Label>
              <Form.Control
                type="password"
                value={passwordFormData.current_password}
                onChange={(e) => setPasswordFormData({...passwordFormData, current_password: e.target.value})}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>New Password *</Form.Label>
              <Form.Control
                type="password"
                value={passwordFormData.new_password}
                onChange={(e) => setPasswordFormData({...passwordFormData, new_password: e.target.value})}
                required
                minLength={8}
              />
              <Form.Text className="text-muted">
                Must be at least 8 characters long.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Confirm New Password *</Form.Label>
              <Form.Control
                type="password"
                value={passwordFormData.confirm_password}
                onChange={(e) => setPasswordFormData({...passwordFormData, confirm_password: e.target.value})}
                required
                minLength={8}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Changing...' : 'Change Password'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Account Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title>Delete Account</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <strong>Warning:</strong> This action cannot be undone!
          </Alert>
          <p>
            Deleting your account will permanently remove:
          </p>
          <ul>
            <li>All your profiles</li>
            <li>All your content scores</li>
            <li>All your trust relationships</li>
            <li>All your network memberships</li>
            <li>Your account data</li>
          </ul>
          <Form.Group className="mb-3">
            <Form.Label>
              Type <strong>DELETE</strong> to confirm:
            </Form.Label>
            <Form.Control
              type="text"
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder="DELETE"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteAccount}
            disabled={loading || deleteConfirmation !== 'DELETE'}
          >
            {loading ? 'Deleting...' : 'Delete My Account'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Payment History Modal */}
      <Modal show={showPaymentHistoryModal} onHide={() => setShowPaymentHistoryModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Payment History</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {paymentHistory.length > 0 ? (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {paymentHistory.map(payment => (
                  <tr key={payment.id}>
                    <td>{new Date(payment.created).toLocaleDateString()}</td>
                    <td>
                      {payment.payment_type === 'premium_subscription' ? 'Premium Subscription' :
                       payment.payment_type === 'renewal' ? 'Renewal' :
                       payment.payment_type === 'refund' ? 'Refund' : payment.payment_type}
                    </td>
                    <td>${payment.amount.toFixed(2)} {payment.currency}</td>
                    <td>
                      <Badge bg={
                        payment.status === 'completed' ? 'success' :
                        payment.status === 'pending' ? 'warning' :
                        payment.status === 'failed' ? 'danger' :
                        payment.status === 'refunded' ? 'info' : 'secondary'
                      }>
                        {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p className="text-center text-muted">No payment history found.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPaymentHistoryModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default Settings
